// The email itself, editable in place. The frame is same-origin, so the editor listens to its document directly.
// Typing updates the data without redrawing (so the cursor stays put). Anything else redraws the email in place.

import { useEffect, useRef } from 'preact/hooks';
import { BLOCK_INFO } from '../../shared/blocks';
import { armDrag, isDragging, setDropTarget, type DragPayload, type DropAction } from './drag';
import { gapAt } from './paths';

export type FrameAction = 'up' | 'down' | 'copy' | 'delete';

interface Props {
  html: string; // full email document with data-* hooks
  version: number; // bump to redraw
  width: number;
  selectedId: string | null;
  onEdit: (id: string, path: string, value: string) => void;
  onSelect: (id: string | null) => void;
  onImage: (id: string, path: string) => void;
  onAction: (act: FrameAction, id: string) => void;
  onDrop: (a: DropAction) => void;
  onUndo: () => void;
  onRedo: () => void;
  onHeight?: (h: number) => void;
  frameRef?: { current: HTMLIFrameElement | null };
}

const EDITOR_CSS = `
html,body{min-height:100%}
[data-edit]{outline:none;cursor:text;border-radius:4px;transition:box-shadow .15s ease,background-color .15s ease}
[data-edit]:hover{box-shadow:0 0 0 1px rgba(62,85,96,.28)}
[data-edit]:focus{box-shadow:0 0 0 2px #a3c4d3;background-color:rgba(163,196,211,.10)}
[data-edit]:empty:before{content:attr(data-ph);color:#9aa7ad;font-style:italic}
[data-img]{cursor:pointer;transition:filter .15s ease}
[data-img]:hover{filter:brightness(.88)}
[data-block]{cursor:pointer}
[data-block]>td{transition:box-shadow .15s ease}
[data-block]:hover>td{box-shadow:inset 0 0 0 1px rgba(62,85,96,.25)}
[data-block].lv-on>td{box-shadow:inset 0 0 0 2px #3e5560}
[data-block]:focus{outline:none}
[data-block]:focus-visible>td{box-shadow:inset 0 0 0 3px #3e5560}
[data-img]:focus{outline:none}
[data-img]:focus-visible{outline:3px solid #3e5560;outline-offset:-3px;filter:brightness(.88)}
[data-block].lv-new>td{animation:lvNew 1.2s ease}
@keyframes lvNew{0%{background-color:rgba(163,196,211,.45)}100%{background-color:transparent}}
a{cursor:text}
.lv-tag{font-size:0;white-space:nowrap;user-select:all;-webkit-user-select:all}
.lv-tag:before{content:attr(data-label);display:inline-block;vertical-align:baseline;font:600 13px/1.5 system-ui,-apple-system,'Segoe UI',sans-serif;padding:0 8px;margin:0 1px;border-radius:999px;background:#d9f2ea;color:#14594a;letter-spacing:0}
#lv-bar{position:absolute;z-index:50;display:none;gap:2px;padding:3px;background:#14212a;border-radius:9px;box-shadow:0 6px 18px rgba(20,33,42,.25)}
#lv-bar button,#lv-fmt button{all:unset;cursor:pointer;width:30px;height:30px;display:grid;place-items:center;border-radius:6px;color:#fff}
#lv-bar button:hover,#lv-fmt button:hover{background:rgba(255,255,255,.14)}
#lv-bar button.del:hover{background:#b3261e}
#lv-bar svg,#lv-fmt svg{width:16px;height:16px;fill:none;stroke:currentColor;stroke-width:2;stroke-linecap:round;stroke-linejoin:round}
#lv-fmt{position:absolute;z-index:51;display:none;gap:2px;padding:3px;background:#14212a;border-radius:9px;box-shadow:0 6px 18px rgba(20,33,42,.25);font:600 14px/1 system-ui,sans-serif}
#lv-fmt button b,#lv-fmt button i{font-size:15px}
#lv-grip{position:absolute;z-index:49;display:none;place-items:center;width:24px;height:32px;border-radius:7px;background:#fff;color:#3e5560;box-shadow:0 1px 5px rgba(20,33,42,.28);cursor:grab;touch-action:none;user-select:none;-webkit-user-select:none;-webkit-touch-callout:none}
#lv-grip:hover{background:#14212a;color:#fff}
#lv-grip svg{width:16px;height:16px;fill:currentColor}
@media (pointer:coarse){#lv-grip{width:38px;height:38px;border-radius:10px}}
#lv-drop{position:absolute;z-index:60;display:none;height:4px;margin-top:-2px;background:#3e5560;border-radius:2px;pointer-events:none}
#lv-drop:before,#lv-drop:after{content:'';position:absolute;top:-4px;width:12px;height:12px;border-radius:50%;background:#3e5560}
#lv-drop:before{left:-6px}#lv-drop:after{right:-6px}
#lv-tip{position:absolute;z-index:61;display:none;transform:translate(-50%,-50%);padding:7px 11px;border-radius:7px;background:#3e5560;color:#fff;font:600 13px/1.1 system-ui,sans-serif;white-space:nowrap;pointer-events:none;box-shadow:0 4px 12px rgba(20,33,42,.25)}
html.lv-dragging #lv-bar,html.lv-dragging #lv-fmt,html.lv-dragging #lv-grip{display:none!important}
html.lv-dragging,html.lv-dragging *{cursor:grabbing!important;-webkit-user-select:none;user-select:none}
[data-block].lv-lift>td{opacity:.3}
[data-img].lv-swap{outline:3px solid #3e5560;outline-offset:-3px;filter:brightness(.8)}
`;

const svg = (d: string) => `<svg viewBox="0 0 24 24">${d.split(' M').map((p, i) => `<path d="${i ? 'M' + p : p}"/>`).join('')}</svg>`;
const BAR = [
  ['up', 'Move up', 'M12 19V5 M5 12l7-7 7 7'],
  ['down', 'Move down', 'M12 5v14 M19 12l-7 7-7-7'],
  ['copy', 'Copy', 'M20 9h-9a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h9a2 2 0 0 0 2-2v-9a2 2 0 0 0-2-2z M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1'],
  ['delete', 'Delete', 'M3 6h18 M8 6V4h8v2 M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6'],
]
  .map(([a, t, d]) => `<button type="button" data-act="${a}" title="${t}" aria-label="${t}" class="${a === 'delete' ? 'del' : ''}">${svg(d)}</button>`)
  .join('');
const GRIP = `<svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="9" cy="6" r="1.7"/><circle cx="15" cy="6" r="1.7"/><circle cx="9" cy="12" r="1.7"/><circle cx="15" cy="12" r="1.7"/><circle cx="9" cy="18" r="1.7"/><circle cx="15" cy="18" r="1.7"/></svg>`;
const FMT = `<button type="button" data-fmt="bold" title="Bold"><b>B</b></button><button type="button" data-fmt="italic" title="Italic"><i>I</i></button><button type="button" data-fmt="link" title="Add a link">${svg(
  'M10 13a5 5 0 0 0 7.5.5l3-3a5 5 0 0 0-7-7l-1.7 1.7 M14 11a5 5 0 0 0-7.5-.5l-3 3a5 5 0 0 0 7 7l1.7-1.7',
)}</button>`;

export function withEditorCss(html: string): string {
  return html.replace('</head>', `<style id="lv-css">${EDITOR_CSS}</style></head>`);
}

// A merge tag like {{first_name}} shows as a small "First name" pill while she edits.
// The pill keeps the real tag as its (invisible) text, so what gets saved is unchanged.
const TAG = /\{\{\s*first[_ ]name\s*\}\}/gi;
const PILL = /<span class="lv-tag"[^>]*>([^<]*)<\/span>/g;

function pillify(el: HTMLElement): void {
  const d = el.ownerDocument;
  const walker = d.createTreeWalker(el, NodeFilter.SHOW_TEXT);
  const hits: Text[] = [];
  for (let n = walker.nextNode(); n; n = walker.nextNode()) {
    if ((n.parentElement as HTMLElement | null)?.closest('.lv-tag')) continue;
    TAG.lastIndex = 0;
    if (TAG.test(n.nodeValue || '')) hits.push(n as Text);
  }
  for (const t of hits) {
    const text = t.nodeValue || '';
    const frag = d.createDocumentFragment();
    let at = 0;
    TAG.lastIndex = 0;
    for (let m = TAG.exec(text); m; m = TAG.exec(text)) {
      if (m.index > at) frag.append(text.slice(at, m.index));
      const pill = d.createElement('span');
      pill.className = 'lv-tag';
      pill.contentEditable = 'false';
      pill.dataset.label = 'First name';
      pill.title = "Each client sees their own first name here";
      pill.textContent = m[0];
      frag.append(pill);
      at = m.index + m[0].length;
    }
    if (at < text.length) frag.append(text.slice(at));
    t.replaceWith(frag);
  }
}

function valueOf(el: HTMLElement): string {
  if (el.dataset.kind === 'rich') return el.innerHTML.replace(PILL, '$1');
  return (el.innerText || '').replace(/\s*\n\s*/g, ' ').replace(/ /g, ' ').trimEnd();
}

export function Canvas(p: Props) {
  const frame = useRef<HTMLIFrameElement>(null);
  const cb = useRef(p);
  cb.current = p;
  const first = useRef(true);
  const initialHtml = useRef(withEditorCss(p.html));

  const doc = () => frame.current?.contentDocument ?? null;

  /** Make fields editable, mark the selection, add the toolbars. Runs after every redraw. */
  const prep = () => {
    const d = doc();
    if (!d) return;
    d.querySelectorAll<HTMLElement>('[data-edit]').forEach((el) => {
      const rich = el.dataset.kind === 'rich';
      el.setAttribute('contenteditable', rich ? 'true' : 'plaintext-only');
      if (!rich && el.contentEditable !== 'plaintext-only') el.setAttribute('contenteditable', 'true');
      el.setAttribute('data-ph', 'Type here');
      el.setAttribute('spellcheck', 'true');
      pillify(el);
    });
    // Keyboard: photos are buttons, and a section with no words or photo can be tabbed to and selected.
    d.querySelectorAll<HTMLElement>('[data-img]').forEach((el) => {
      el.setAttribute('title', 'Click to change the photo');
      el.setAttribute('tabindex', '0');
      // A photo behind words (the big top photo) holds editable text, so it is a group, not a button.
      if (el.querySelector('[data-edit]')) {
        el.setAttribute('role', 'group');
        el.setAttribute('aria-label', 'Photo. Press Enter to change it.');
      } else {
        el.setAttribute('role', 'button');
        el.setAttribute('aria-label', `Change photo${el.getAttribute('alt') ? `: ${el.getAttribute('alt')}` : ''}`);
      }
    });
    d.querySelectorAll<HTMLElement>('[data-block]').forEach((el) => {
      if (el.querySelector('[data-edit], [data-img]')) return;
      el.setAttribute('tabindex', '0');
      el.setAttribute('aria-label', `${BLOCK_INFO.find((b) => b.type === el.dataset.type)?.label ?? 'Section'}. Press Enter to select.`);
    });
    if (!d.getElementById('lv-bar')) {
      const bar = d.createElement('div');
      bar.id = 'lv-bar';
      bar.innerHTML = BAR;
      d.body.appendChild(bar);
      const fmt = d.createElement('div');
      fmt.id = 'lv-fmt';
      fmt.innerHTML = FMT;
      d.body.appendChild(fmt);
      const grip = d.createElement('div');
      grip.id = 'lv-grip';
      grip.title = 'Drag to move this section';
      grip.setAttribute('aria-label', 'Drag to move this section');
      grip.innerHTML = GRIP;
      d.body.appendChild(grip);
      const line = d.createElement('div');
      line.id = 'lv-drop';
      d.body.appendChild(line);
      const tip = d.createElement('div');
      tip.id = 'lv-tip';
      d.body.appendChild(tip);
    }
    mark();
    measure();
  };

  const mark = () => {
    const d = doc();
    if (!d) return;
    const id = cb.current.selectedId;
    d.querySelectorAll<HTMLElement>('[data-block]').forEach((b) => b.classList.toggle('lv-on', b.dataset.block === id));
    placeGrip();
    const bar = d.getElementById('lv-bar');
    const el = id ? d.querySelector<HTMLElement>(`[data-block="${id}"]`) : null;
    if (!bar) return;
    if (!el) {
      bar.style.display = 'none';
      return;
    }
    const r = el.getBoundingClientRect();
    const w = d.defaultView!;
    bar.style.display = 'flex';
    bar.style.top = `${r.top + w.scrollY + 8}px`;
    bar.style.left = `${Math.max(4, r.right + w.scrollX - bar.offsetWidth - 8)}px`;
  };

  // The grip sits beside the section under the mouse, or beside the selected one.
  const hoverId = useRef<string | null>(null);
  const placeGrip = () => {
    const d = doc();
    const grip = d?.getElementById('lv-grip');
    if (!d || !grip) return;
    const id = hoverId.current ?? cb.current.selectedId;
    const el = id ? d.querySelector<HTMLElement>(`[data-block="${id}"]`) : null;
    if (!el || isDragging()) {
      grip.style.display = 'none';
      return;
    }
    const r = el.getBoundingClientRect();
    const w = d.defaultView!;
    grip.dataset.id = id!;
    grip.style.display = 'grid';
    const gw = grip.offsetWidth;
    grip.style.top = `${r.top + w.scrollY + 8}px`;
    grip.style.left = `${r.left >= gw + 6 ? r.left - gw - 3 : r.left + 8}px`;
  };

  // ----- dropping -----
  const rows = () => [...(doc()?.querySelectorAll<HTMLElement>('[data-block]') ?? [])];
  const clearDrop = () => {
    const d = doc();
    if (!d) return;
    for (const id of ['lv-drop', 'lv-tip']) {
      const el = d.getElementById(id);
      if (el) el.style.display = 'none';
    }
    d.querySelectorAll('.lv-swap').forEach((el) => el.classList.remove('lv-swap'));
  };

  /** Where a drop at (x, y) in frame coordinates would land. gap -1 = where it already is. */
  type Spot = { gap: number } | { img: HTMLElement } | null;
  const spotAt = (x: number, y: number, mode: 'section' | 'files', moving?: string): Spot => {
    const d = doc();
    const f = frame.current;
    if (!d || !f) return null;
    if (x < -40 || x > f.offsetWidth + 40) return null; // off the email
    const list = rows();
    const rects = list.map((r) => r.getBoundingClientRect());
    if (mode === 'files') {
      // Over a photo (but not at a section's edge) means "replace this photo".
      const nearEdge = rects.some((r) => Math.abs(y - r.top) < 22 || Math.abs(y - r.bottom) < 22);
      const img = nearEdge ? null : (d.elementFromPoint(x, y) as HTMLElement | null)?.closest<HTMLElement>('[data-img]');
      if (img) return { img };
    }
    const gap = gapAt(rects, y);
    if (moving) {
      const from = list.findIndex((r) => r.dataset.block === moving);
      if (gap === from || gap === from + 1) return { gap: -1 };
    }
    return { gap };
  };

  const showSpot = (spot: Spot, label: string) => {
    clearDrop();
    const d = doc();
    if (!d || !spot) return;
    const w = d.defaultView!;
    const tip = d.getElementById('lv-tip');
    const line = d.getElementById('lv-drop');
    if (!tip || !line) return;
    if ('img' in spot) {
      spot.img.classList.add('lv-swap');
      const r = spot.img.getBoundingClientRect();
      tip.textContent = 'Replace this photo';
      tip.style.display = 'block';
      tip.style.left = `${r.left + r.width / 2 + w.scrollX}px`;
      tip.style.top = `${r.top + r.height / 2 + w.scrollY}px`;
      return;
    }
    const list = rows();
    if (spot.gap < 0 || !list.length) return;
    const first = list[0].getBoundingClientRect();
    const top = spot.gap < list.length ? list[spot.gap].getBoundingClientRect().top : list[list.length - 1].getBoundingClientRect().bottom;
    line.style.display = 'block';
    line.style.left = `${first.left + w.scrollX + 6}px`;
    line.style.width = `${first.width - 12}px`;
    line.style.top = `${top + w.scrollY}px`;
    tip.textContent = label;
    tip.style.display = 'block';
    tip.style.left = `${first.left + first.width / 2 + w.scrollX}px`;
    tip.style.top = `${top + w.scrollY + (spot.gap === 0 ? 22 : -22)}px`;
  };

  const sectionLabel = (p: DragPayload) => (p.kind === 'move' ? 'Move here' : 'Add it here');

  useEffect(() => {
    const local = (x: number, y: number): [number, number] => {
      const r = frame.current!.getBoundingClientRect();
      return [x - r.left, y - r.top];
    };
    const spotFor = (x: number, y: number, p: DragPayload) => spotAt(...local(x, y), 'section', p.kind === 'move' ? p.id : undefined);
    setDropTarget({
      frame: () => frame.current,
      scroller: () => (frame.current?.closest('.ed-canvas') as HTMLElement | null) ?? null,
      over(x, y, p) {
        const d = doc();
        if (!d || !frame.current) return;
        d.documentElement.classList.add('lv-dragging');
        if (p.kind === 'move') d.querySelector(`[data-block="${p.id}"]`)?.classList.add('lv-lift');
        showSpot(spotFor(x, y, p), sectionLabel(p));
      },
      end(x, y, p, commit) {
        const d = doc();
        const spot = commit && frame.current ? spotFor(x, y, p) : null;
        clearDrop();
        d?.documentElement.classList.remove('lv-dragging');
        d?.querySelectorAll('.lv-lift').forEach((el) => el.classList.remove('lv-lift'));
        placeGrip();
        if (!spot || !('gap' in spot) || spot.gap < 0) return;
        if (p.kind === 'add') cb.current.onDrop({ kind: 'add', type: p.type, index: spot.gap });
        else cb.current.onDrop({ kind: 'move', id: p.id, index: spot.gap });
      },
    });
    return () => setDropTarget(null);
  }, []);

  const measure = () => {
    const d = doc();
    const f = frame.current;
    if (!d || !f) return;
    const h = Math.max(d.documentElement.scrollHeight, d.body?.scrollHeight ?? 0);
    if (Math.abs(f.offsetHeight - h) > 1) {
      f.style.height = `${h}px`;
      cb.current.onHeight?.(h);
    }
  };

  const showFmt = (field: HTMLElement | null) => {
    const d = doc();
    const fmt = d?.getElementById('lv-fmt');
    if (!d || !fmt) return;
    if (!field || field.dataset.kind !== 'rich') {
      fmt.style.display = 'none';
      return;
    }
    const r = field.getBoundingClientRect();
    const w = d.defaultView!;
    fmt.style.display = 'flex';
    fmt.style.top = `${Math.max(0, r.top + w.scrollY - 42)}px`;
    fmt.style.left = `${r.left + w.scrollX}px`;
  };

  const attach = () => {
    const d = doc();
    const w = frame.current?.contentWindow as (Window & typeof globalThis) | null;
    if (!d || !w) return;
    d.addEventListener('click', (e) => {
      const t = e.target as HTMLElement;
      const act = t.closest<HTMLElement>('[data-act]');
      if (act) {
        e.preventDefault();
        const id = cb.current.selectedId;
        if (id) cb.current.onAction(act.dataset.act as FrameAction, id);
        return;
      }
      if (t.closest('#lv-fmt')) return;
      const grip = t.closest<HTMLElement>('#lv-grip');
      if (grip) {
        if (grip.dataset.id) cb.current.onSelect(grip.dataset.id);
        return;
      }
      if (t.closest('a')) e.preventDefault();
      // Words on top of a photo are for typing: clicking them must not open the photo picker.
      const img = t.closest('[data-edit]') ? null : t.closest<HTMLElement>('[data-img]');
      const block = t.closest<HTMLElement>('[data-block]');
      if (block) cb.current.onSelect(block.dataset.block!);
      else cb.current.onSelect(null);
      if (img) {
        const [id, path] = img.dataset.img!.split('|');
        cb.current.onImage(id, path);
      }
    });
    d.addEventListener('mousedown', (e) => {
      const t = e.target as HTMLElement;
      const f = t.closest<HTMLElement>('[data-fmt]');
      if (!f) return;
      e.preventDefault(); // keep the text selection
      const cmd = f.dataset.fmt!;
      if (cmd === 'link') {
        const sel = d.getSelection();
        if (!sel || sel.isCollapsed) {
          w.alert('Select the words you want to turn into a link first.');
          return;
        }
        const url = w.prompt('Link to which web address?', 'https://');
        if (url && /^(https?:\/\/|mailto:|tel:)/i.test(url.trim())) d.execCommand('createLink', false, url.trim());
      } else d.execCommand(cmd);
    });
    d.addEventListener('input', (e) => {
      const el = (e.target as HTMLElement).closest<HTMLElement>('[data-edit]');
      if (!el) return;
      const [id, path] = el.dataset.edit!.split('|');
      cb.current.onEdit(id, path, valueOf(el));
      measure();
      mark();
    });
    d.addEventListener('focusin', (e) => {
      const t = e.target as HTMLElement;
      showFmt(t.closest<HTMLElement>('[data-edit]'));
      // Typing into a section (by tap, click or Tab) selects it, so its options match.
      const id = t.closest<HTMLElement>('[data-edit]')?.closest<HTMLElement>('[data-block]')?.dataset.block;
      if (id && id !== cb.current.selectedId) cb.current.onSelect(id);
    });
    d.addEventListener('focusout', () => setTimeout(() => showFmt(d.activeElement?.closest<HTMLElement>('[data-edit]') ?? null), 0));
    d.addEventListener('paste', (e) => {
      const el = (e.target as HTMLElement).closest<HTMLElement>('[data-edit]');
      if (!el) return;
      e.preventDefault();
      let text = e.clipboardData?.getData('text/plain') || '';
      if (el.dataset.kind !== 'rich') text = text.replace(/\s*\n\s*/g, ' ');
      d.execCommand('insertText', false, text);
    });
    d.addEventListener('keydown', (e) => {
      const mod = e.ctrlKey || e.metaKey;
      if (mod && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        (e.shiftKey ? cb.current.onRedo : cb.current.onUndo)();
        return;
      }
      if (mod && e.key.toLowerCase() === 'y') {
        e.preventDefault();
        cb.current.onRedo();
        return;
      }
      const t = e.target as HTMLElement;
      if ((e.key === 'Enter' || e.key === ' ') && (t.dataset?.img || t.dataset?.block)) {
        e.preventDefault();
        const id = t.dataset.block ?? t.closest<HTMLElement>('[data-block]')?.dataset.block;
        if (id) cb.current.onSelect(id);
        if (t.dataset.img) {
          const [bid, path] = t.dataset.img.split('|');
          cb.current.onImage(bid, path);
        }
        return;
      }
      const el = t.closest?.<HTMLElement>('[data-edit]');
      if (!el) return;
      if (e.key === 'Enter') {
        e.preventDefault();
        if (el.dataset.kind === 'rich') d.execCommand('insertLineBreak');
        else el.blur();
      }
      if (e.key === 'Escape') el.blur();
      if (mod && e.key.toLowerCase() === 'b' && el.dataset.kind !== 'rich') e.preventDefault();
    });
    // Grip: follows the mouse. Drag it to move the section.
    d.addEventListener('pointerover', (e) => {
      if (e.pointerType !== 'mouse' || isDragging()) return;
      const t = e.target as HTMLElement;
      if (t.closest('#lv-grip, #lv-bar')) return;
      // Off a section (like the margin the grip sits in), the grip stays where it is.
      const id = t.closest<HTMLElement>('[data-block]')?.dataset.block;
      if (id && id !== hoverId.current) {
        hoverId.current = id;
        placeGrip();
      }
    });
    d.documentElement.addEventListener('mouseleave', () => {
      hoverId.current = null;
      placeGrip();
    });
    d.addEventListener('pointerdown', (e) => {
      const grip = (e.target as HTMLElement).closest<HTMLElement>('#lv-grip');
      if (!grip?.dataset.id) return;
      e.preventDefault();
      const row = d.querySelector<HTMLElement>(`[data-block="${grip.dataset.id}"]`);
      const label = BLOCK_INFO.find((b) => b.type === row?.dataset.type)?.label ?? 'Section';
      armDrag(e, { kind: 'move', id: grip.dataset.id, label }, { hold: 0 });
    });

    // Photo files dragged in from the computer.
    let fileTimer: number | undefined;
    const hasFiles = (e: DragEvent) => !!e.dataTransfer && [...e.dataTransfer.types].includes('Files');
    d.addEventListener('dragover', (e) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      e.dataTransfer!.dropEffect = 'copy';
      showSpot(spotAt(e.clientX, e.clientY, 'files'), 'Add your photo here');
      clearTimeout(fileTimer);
      fileTimer = w.setTimeout(clearDrop, 250); // dragleave is unreliable; this clears once the drag leaves
    });
    d.addEventListener('drop', (e) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      clearTimeout(fileTimer);
      const spot = spotAt(e.clientX, e.clientY, 'files');
      clearDrop();
      const files = [...(e.dataTransfer?.files ?? [])];
      if (!files.length || !spot) return;
      if ('img' in spot) {
        const [id, path] = spot.img.dataset.img!.split('|');
        cb.current.onDrop({ kind: 'replace', file: files[0], id, path });
      } else if (spot.gap >= 0) cb.current.onDrop({ kind: 'files', files, index: spot.gap });
    });

    w.addEventListener('resize', () => {
      mark();
      measure();
    });
    // Photos load after the first measure.
    d.addEventListener('load', () => measure(), true);
    const ro = new w.ResizeObserver(() => {
      measure();
      mark();
    });
    ro.observe(d.documentElement);
  };

  // First load.
  const onLoad = () => {
    attach();
    prep();
    first.current = false;
  };

  // Redraw in place when the version changes.
  useEffect(() => {
    if (first.current) return;
    const d = doc();
    if (!d) return;
    const parsed = new DOMParser().parseFromString(withEditorCss(p.html), 'text/html');
    const active = d.activeElement as HTMLElement | null;
    const editing = active?.dataset?.edit;
    d.documentElement.innerHTML = parsed.documentElement.innerHTML;
    prep();
    if (editing) d.querySelector<HTMLElement>(`[data-edit="${editing}"]`)?.focus();
  }, [p.version]);

  useEffect(() => {
    mark();
  }, [p.selectedId]);

  useEffect(() => {
    // Width change: the email reflows.
    setTimeout(() => {
      measure();
      mark();
    }, 30);
  }, [p.width]);

  return <iframe
      ref={(el) => {
        frame.current = el;
        if (p.frameRef) p.frameRef.current = el;
      }}
      class="canvas-frame" title="Your email" srcdoc={initialHtml.current} onLoad={onLoad} style={{ width: `${p.width}px` }} />;
}

/** Scroll the editor so a block is in view and flash it. */
export function revealBlock(frame: HTMLIFrameElement | null, scroller: HTMLElement | null, id: string): void {
  const d = frame?.contentDocument;
  const el = d?.querySelector<HTMLElement>(`[data-block="${id}"]`);
  if (!el || !scroller || !frame) return;
  const top = frame.offsetTop + el.getBoundingClientRect().top - 90;
  scroller.scrollTo({ top, behavior: 'smooth' });
  el.classList.remove('lv-new');
  void el.offsetWidth;
  el.classList.add('lv-new');
}
